/* eslint-disable complexity */
import { useEffect, useMemo, useState } from 'react';
import { Box, Divider, IconButton, Typography, useTheme } from '@mui/material';
import { ChevronLeft } from '@mui/icons-material';
import PropTypes from 'prop-types';
import { get } from 'lodash';

import SectionWrapper from '../SectionWrapper/SectionWrapper';
import { generateRouteMapStaticImageUrlByParcelCode } from '../../helpers/map';
import { StringUtil } from '../../utils';
import { SHIPMENT_FIELDS } from '../../constants/forms';
import { shipmentsApis } from '../../apis';
import IssueSection from './components/IssueSection';
import TrackingSection from './components/TrackingSection';
import AddressSection from './components/AddressSection';
import DeliveryStatusSection from './components/DeliveryStatusSection';
import EventsSection from './components/EventsSection';

const OrderDetails = ({
  parcel,
  trackingImages = [],
  summaryTitle,
  footerButtons,
  onBackClick,
  showBackButton = true,
  isFullView = true,
  styles = {},
}) => {
  const theme = useTheme();
  const [mapHidden, setMapHidden] = useState(true);

  const {
    parcelCode,
    collectionDetails,
    deliveryDetails,
    parcelEvents = [],
    parcelNumber,
    parcelStatus,
    parcelStatusHtml,
    product,
    service,
    issueDetails,
  } = { ...parcel };
  let images = trackingImages;
  const parcelNumberWithoutChecksum = (parcelNumber || '').slice(0, -1);

  const contactNameOrOrganisation =
    deliveryDetails?.notificationDetails?.contactName ??
    deliveryDetails?.address?.organisation;
  const deliveryAndCollectionAddressLine1 =
    collectionDetails?.address?.street && deliveryDetails?.address?.street; // NOTE: changed because not all countries require postcode
  const deliveryAddressCoordinates = get(
    deliveryDetails,
    'address.addressPoint'
  );

  if (parcelEvents?.length > 0 && images.length === 0) {
    const firstEvent = parcelEvents[0];
    images = firstEvent?.images || [];
  }

  const mapSrc = useMemo(() => {
    if (deliveryAndCollectionAddressLine1) {
      return generateRouteMapStaticImageUrlByParcelCode({
        parcelCode,
        width: 600,
        height: 340,
      });
    }
    return null;
  }, [parcelCode, deliveryAndCollectionAddressLine1]);

  useEffect(() => {
    const fetchShipmentData = async () => {
      try {
        if (parcelNumberWithoutChecksum) {
          const shipmentData = await shipmentsApis.getShipmentByParcelNumber(
            StringUtil.trimAllSpaces(parcelNumberWithoutChecksum)
          );
          const pickupPointLocationCode = !!get(
            shipmentData,
            SHIPMENT_FIELDS.PICKUP_LOCATION_CODE.KEY
          );
          setMapHidden(pickupPointLocationCode);
        }
      } catch (error) {
        setMapHidden(false);
      }
    };

    fetchShipmentData();
  }, [parcelNumberWithoutChecksum]);

  return (
    <>
      {parcel ? (
        <SectionWrapper
          titleLeftComponent={
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'row',
                alignContent: 'center',
                justifyContent: 'flex-start',
                alignItems: 'center',
              }}
            >
              {showBackButton && (
                <IconButton
                  aria-label='back'
                  sx={{ mr: 0.5, p: 0.5 }}
                  onClick={onBackClick}
                >
                  <ChevronLeft />
                </IconButton>
              )}
              <Box>
                {contactNameOrOrganisation && !summaryTitle && (
                  <Typography>{contactNameOrOrganisation}</Typography>
                )}
                {summaryTitle && (
                  <Typography variant='h3'>{summaryTitle}</Typography>
                )}
                <Typography
                  variant='body2'
                  color='textSecondary'
                  sx={{
                    fontFamily: 'Roboto',
                    m: 0,
                  }}
                >
                  {parcelNumberWithoutChecksum}
                  {service?.serviceDescription
                    ? ` • ${StringUtil.capitalizeFirstLetters(
                        StringUtil.capitalizeDPD(service.serviceDescription)
                      )}`
                    : ''}
                  {product?.productDescription
                    ? ` - ${StringUtil.capitalizeFirstLetters(
                        product.productDescription
                      )}`
                    : ''}
                </Typography>
              </Box>
            </Box>
          }
          titleRightComponent={
            parcelStatus && (
              <Typography
                color={theme.vars.palette.parcelStatus[parcelStatus]}
                sx={{ ml: { xs: 0, md: 2 }, mt: { xs: 2, md: 0 } }}
              >
                {parcelStatus}
              </Typography>
            )
          }
          sx={{ height: 'auto', ...styles }}
        >
          <Box>
            {!mapHidden && (
              <TrackingSection
                mapSrc={mapSrc}
                images={images}
                parcelNumber={parcelNumberWithoutChecksum}
                coordinates={deliveryAddressCoordinates}
              />
            )}
            <DeliveryStatusSection
              parcelStatus={parcelStatus}
              parcelStatusHtml={parcelStatusHtml}
              onRouteElectric={isFullView && deliveryDetails?.onRouteElectric}
              withBorder={isFullView}
            />
            {isFullView && (
              <>
                <IssueSection issueDetails={issueDetails} />
                {deliveryAndCollectionAddressLine1 && (
                  <AddressSection
                    collectionDetails={collectionDetails}
                    deliveryDetails={deliveryDetails}
                  />
                )}
                <EventsSection parcelEvents={parcelEvents} theme={theme} />
              </>
            )}
          </Box>
          {isFullView && <Divider sx={{ mt: 2, marginX: -2 }} />}
          {footerButtons && (
            <Box
              sx={{
                display: 'flex',
                flexDirection: { md: 'row', xs: 'column' },
                justifyContent: { md: 'flex-end', xs: 'center' },
                pt: 1,
                gap: 1,
              }}
            >
              {footerButtons}
            </Box>
          )}
        </SectionWrapper>
      ) : null}
    </>
  );
};

OrderDetails.propTypes = {
  parcel: PropTypes.object,
  trackingImages: PropTypes.array,
  summaryTitle: PropTypes.string,
  footerButtons: PropTypes.node,
  onBackClick: PropTypes.func,
  showBackButton: PropTypes.bool,
  isFullView: PropTypes.bool,
};

export default OrderDetails;
